import React, { useState, useEffect, useRef } from "react";

const CelebrationOverlay = ({ show }) => {
  const canvasRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const frameRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!show) { startedRef.current = false; return; }
    if (startedRef.current) return;
    startedRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W;
    canvas.height = H;

    const colors = ["#FF6B6B","#FFE66D","#4ECDC4","#45B7D1","#96CEB4","#FFEAA7","#DDA0DD","#FF9FF3","#54A0FF","#5F27CD","#FF9F43","#EE5A24","#00D2D3","#01A3A4","#F368E0"];
    const pts = [];

    // Create firework bursts from multiple points
    const burstPoints = [
      { x: W * 0.2, y: H * 0.25 }, { x: W * 0.5, y: H * 0.15 },
      { x: W * 0.8, y: H * 0.25 }, { x: W * 0.35, y: H * 0.3 },
      { x: W * 0.65, y: H * 0.2 }
    ];
    burstPoints.forEach((bp, bIdx) => {
      setTimeout(() => {
        for (let i = 0; i < 35; i++) {
          const angle = (Math.PI * 2 * i) / 35 + Math.random() * 0.3;
          const speed = 2.5 + Math.random() * 4;
          pts.push({ x: bp.x, y: bp.y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed - 1, life: 1, decay: 0.008 + Math.random() * 0.008, color: colors[Math.floor(Math.random() * colors.length)], size: 2.5 + Math.random() * 3, type: "spark", trail: [] });
        }
      }, bIdx * 350);
    });

    // Confetti pieces falling from top
    for (let i = 0; i < 80; i++) {
      pts.push({
        x: Math.random() * W, y: -20 - Math.random() * 200,
        vx: (Math.random() - 0.5) * 2, vy: 1.5 + Math.random() * 3,
        life: 1, decay: 0.002 + Math.random() * 0.002,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 4 + Math.random() * 6, type: "confetti",
        rotation: Math.random() * 360, rotSpeed: (Math.random() - 0.5) * 8,
        wobble: Math.random() * Math.PI * 2, wobbleSpeed: 0.03 + Math.random() * 0.03
      });
    }

    // Party blower emoji particles from bottom corners
    const blowerEmojis = ["🎉","🎊","🥳","🎈","🎺","🎶","✨","💫","⭐"];
    for (let i = 0; i < 18; i++) {
      const fromLeft = i % 2 === 0;
      pts.push({
        x: fromLeft ? 40 + Math.random() * 60 : W - 40 - Math.random() * 60,
        y: H - 40 - Math.random() * 40,
        vx: fromLeft ? 2 + Math.random() * 4 : -(2 + Math.random() * 4),
        vy: -(4 + Math.random() * 6),
        life: 1, decay: 0.005 + Math.random() * 0.004,
        emoji: blowerEmojis[Math.floor(Math.random() * blowerEmojis.length)],
        size: 16 + Math.random() * 16, type: "emoji",
        rotation: Math.random() * 30 - 15, rotSpeed: (Math.random() - 0.5) * 3
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      let alive = 0;
      pts.forEach(p => {
        if (p.life <= 0) return;
        alive++;
        p.life -= p.decay;
        p.x += p.vx;
        p.vy += p.type === "confetti" ? 0.02 : p.type === "emoji" ? 0.12 : 0.06;
        p.y += p.vy;
        if (p.type === "confetti") {
          p.wobble += p.wobbleSpeed;
          p.x += Math.sin(p.wobble) * 1.2;
          p.vx *= 0.999;
        }
        const alpha = Math.max(0, p.life);
        ctx.globalAlpha = alpha;

        if (p.type === "spark") {
          p.trail.push({ x: p.x, y: p.y });
          if (p.trail.length > 5) p.trail.shift();
          p.trail.forEach((t, ti) => {
            ctx.globalAlpha = alpha * (ti / p.trail.length) * 0.5;
            ctx.beginPath();
            ctx.arc(t.x, t.y, p.size * 0.5, 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.fill();
          });
          ctx.globalAlpha = alpha;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 0.4, 0, Math.PI * 2);
          ctx.fillStyle = "#fff";
          ctx.globalAlpha = alpha * 0.6;
          ctx.fill();
        } else if (p.type === "confetti") {
          p.rotation += p.rotSpeed;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation * Math.PI / 180);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
          ctx.restore();
        } else if (p.type === "emoji") {
          p.rotation += p.rotSpeed;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation * Math.PI / 180);
          ctx.font = p.size + "px serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(p.emoji, 0, 0);
          ctx.restore();
        }
      });
      ctx.globalAlpha = 1;
      if (alive > 0) frameRef.current = requestAnimationFrame(animate);
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [show]);

  if (!show) return null;
  return (<canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", pointerEvents: "none", zIndex: 99999 }} />);
};

export default CelebrationOverlay;
